import {
  PT_P910BT_HEAD_PINS,
  PT_P910BT_RASTER_BYTES,
  PT_P910BT_USB_PRODUCT_ID,
  PT_P910BT_USB_VENDOR_ID,
  getTapeSpec,
  isTapeMm,
  type TapeMm,
} from "@/lib/artikelEtikett";
import { canvasColumnIsDark, type RenderedArtikelEtikett } from "@/lib/artikelEtikettRender";

const STATUS_LENGTH = 32;

export type PtPageRole = "single" | "first" | "middle" | "last";

export function packRasterLine(columnDark: boolean[], leftPins: number): Uint8Array {
  const bytes = new Uint8Array(PT_P910BT_RASTER_BYTES);
  for (let i = 0; i < columnDark.length; i++) {
    if (!columnDark[i]) continue;
    const pin = leftPins + i;
    if (pin < 0 || pin >= PT_P910BT_HEAD_PINS) continue;
    const byteIndex = pin >> 3;
    const bit = 7 - (pin & 7);
    const current = bytes[byteIndex] ?? 0;
    bytes[byteIndex] = current | (1 << bit);
  }
  return bytes;
}

export function canvasToRasterColumns(label: RenderedArtikelEtikett, tapeMm: TapeMm): Uint8Array[] {
  const spec = getTapeSpec(tapeMm);
  const columns: Uint8Array[] = [];
  for (let x = 0; x < label.canvas.width; x++) {
    const dark: boolean[] = [];
    for (let y = 0; y < spec.printDots; y++) {
      dark.push(canvasColumnIsDark(label.canvas, x, y));
    }
    columns.push(packRasterLine(dark, spec.leftPins));
  }
  return columns;
}

export function pageRoleForIndex(index: number, total: number): PtPageRole {
  if (total <= 1) return "single";
  if (index === 0) return "first";
  if (index === total - 1) return "last";
  return "middle";
}

function pageFlag(role: PtPageRole): number {
  if (role === "first") return 0;
  if (role === "middle") return 1;
  return 2;
}

export function buildPrintInfoCommand(tapeMm: TapeMm, rasterCount: number, role: PtPageRole): Uint8Array {
  const n1 = 0x86; // KIND + WIDTH + RECOVER
  return new Uint8Array([
    0x1b,
    0x69,
    0x7a,
    n1,
    0x00,
    tapeMm,
    0x00,
    rasterCount & 0xff,
    (rasterCount >> 8) & 0xff,
    (rasterCount >> 16) & 0xff,
    (rasterCount >> 24) & 0xff,
    pageFlag(role),
    0x00,
  ]);
}

export function concatBytes(parts: Uint8Array[]): Uint8Array {
  const total = parts.reduce((sum, part) => sum + part.length, 0);
  const out = new Uint8Array(total);
  let offset = 0;
  for (const part of parts) {
    out.set(part, offset);
    offset += part.length;
  }
  return out;
}

/** Kompletter PT-P910BT-Auftrag inkl. Init – für USB und Windows-RAW. */
export function buildPtP910btJob(labels: RenderedArtikelEtikett[], tapeMm: TapeMm): Uint8Array {
  if (!labels.length) throw new Error("Keine Etiketten zum Drucken.");
  const parts: Uint8Array[] = [new Uint8Array(200), new Uint8Array([0x1b, 0x40])];
  for (let i = 0; i < labels.length; i++) {
    const label = labels[i];
    if (!label) continue;
    const columns = canvasToRasterColumns(label, tapeMm);
    parts.push(buildPtP910btPage(columns, tapeMm, pageRoleForIndex(i, labels.length)));
  }
  return concatBytes(parts);
}

export function buildPtP910btPage(columns: Uint8Array[], tapeMm: TapeMm, role: PtPageRole): Uint8Array {
  const parts: number[] = [
    0x1b, 0x69, 0x61, 0x01, // raster mode
    0x1b, 0x69, 0x21, 0x00, // status notify
    ...buildPrintInfoCommand(tapeMm, columns.length, role),
    0x1b, 0x69, 0x4d, 0x40, // auto cut
    0x1b, 0x69, 0x41, 0x01, // cut each label
    0x1b, 0x69, 0x4b, 0x08, // no chain printing
    0x1b, 0x69, 0x64, 0x0e, 0x00, // 1 mm margin
    0x4d, 0x00, // no compression
  ];
  for (const column of columns) {
    parts.push(0x47, column.length & 0xff, (column.length >> 8) & 0xff, ...column);
  }
  parts.push(role === "last" || role === "single" ? 0x1a : 0x0c);
  return new Uint8Array(parts);
}

export function isWebUsbAvailable(): boolean {
  return typeof navigator !== "undefined" && Boolean(navigator.usb);
}

function describeStatusError(status: Uint8Array): string {
  const messages: string[] = [];
  const err1 = status[8] ?? 0;
  const err2 = status[9] ?? 0;
  if (err1 & 0x01) messages.push("kein Band eingelegt");
  if (err1 & 0x04) messages.push("Schneider klemmt");
  if (err1 & 0x08) messages.push("Akku schwach");
  if (err2 & 0x01) messages.push("falsche Bandbreite");
  if (err2 & 0x10) messages.push("Deckel offen");
  if (err2 & 0x20) messages.push("Drucker überhitzt");
  return messages.join(", ") || "unbekannter Druckerfehler";
}

interface UsbEndpoints {
  device: USBDevice;
  out: number;
  in: number | null;
}

async function claimPrinter(): Promise<UsbEndpoints> {
  if (!navigator.usb) {
    throw new Error("Dieser Browser unterstützt keinen Direktdruck per USB (Chrome oder Edge verwenden).");
  }
  const device = await navigator.usb.requestDevice({
    filters: [
      { vendorId: PT_P910BT_USB_VENDOR_ID, productId: PT_P910BT_USB_PRODUCT_ID },
      { vendorId: PT_P910BT_USB_VENDOR_ID },
    ],
  });
  await device.open();
  if (!device.configuration) {
    await device.selectConfiguration(1);
  }
  const usbInterface =
    device.configuration?.interfaces.find((iface) =>
      iface.alternates.some((alt) => alt.interfaceClass === 7 || alt.endpoints.length > 0),
    ) ?? device.configuration?.interfaces[0];
  if (!usbInterface) {
    throw new Error("USB-Schnittstelle des PT-P910BT wurde nicht gefunden.");
  }
  try {
    await device.claimInterface(usbInterface.interfaceNumber);
  } catch {
    throw new Error(
      "Der PT-P910BT ist belegt (meist durch den Brother-Treiber). Bitte „Über Systemdrucker“ verwenden oder den Drucker in Chrome erneut freigeben.",
    );
  }
  const alternate = usbInterface.alternates[0];
  const out = alternate?.endpoints.find((endpoint) => endpoint.direction === "out");
  const inn = alternate?.endpoints.find((endpoint) => endpoint.direction === "in");
  if (!out) {
    throw new Error("USB-Ausgabekanal des Druckers fehlt.");
  }
  return { device, out: out.endpointNumber, in: inn?.endpointNumber ?? null };
}

async function writeUsb(conn: UsbEndpoints, data: Uint8Array): Promise<void> {
  const chunkSize = 4096;
  for (let offset = 0; offset < data.length; offset += chunkSize) {
    await conn.device.transferOut(conn.out, data.subarray(offset, offset + chunkSize));
  }
}

async function readStatus(conn: UsbEndpoints): Promise<Uint8Array | null> {
  if (conn.in == null) return null;
  const result = await conn.device.transferIn(conn.in, STATUS_LENGTH);
  if (!result.data) return null;
  return new Uint8Array(result.data.buffer);
}

export async function printLabelsOnPtP910bt(
  labels: RenderedArtikelEtikett[],
  selectedTapeMm: TapeMm,
): Promise<{ usedTapeMm: TapeMm }> {
  if (!labels.length) throw new Error("Keine Etiketten zum Drucken.");
  const conn = await claimPrinter();
  try {
    await writeUsb(conn, new Uint8Array(200));
    await writeUsb(conn, new Uint8Array([0x1b, 0x40]));
    await writeUsb(conn, new Uint8Array([0x1b, 0x69, 0x53]));
    const status = await Promise.race([
      readStatus(conn),
      new Promise<null>((resolve) => {
        window.setTimeout(() => resolve(null), 1500);
      }),
    ]);
    const reported = status?.[10];
    const tapeMm = reported != null && isTapeMm(reported) ? reported : selectedTapeMm;
    if (status && (status[8] || status[9])) {
      throw new Error(`Drucker meldet: ${describeStatusError(status)}`);
    }

    for (let i = 0; i < labels.length; i++) {
      const label = labels[i];
      if (!label) continue;
      const columns = canvasToRasterColumns(label, tapeMm);
      const page = buildPtP910btPage(columns, tapeMm, pageRoleForIndex(i, labels.length));
      await writeUsb(conn, page);
    }

    if (conn.in != null) {
      const deadline = Date.now() + 30000;
      while (Date.now() < deadline) {
        const done = await Promise.race([
          readStatus(conn),
          new Promise<null>((resolve) => {
            window.setTimeout(() => resolve(null), 2000);
          }),
        ]);
        if (!done) break;
        if ((done[18] ?? 0) === 0x01) break;
        if ((done[18] ?? 0) === 0x02) {
          throw new Error(`Druckfehler: ${describeStatusError(done)}`);
        }
      }
    }
    return { usedTapeMm: tapeMm };
  } finally {
    try {
      await conn.device.close();
    } catch {
      // Gerät kann nach dem Druck bereits getrennt sein.
    }
  }
}
